import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import BANNERS_API_V1 from 'api/banners/v1'
import styles from './slider-banner.module.scss'
import { Grid, Typography } from 'gu-ui-kit'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import NavArrows from 'components/Banners/SliderBanner/subcomponents/NavArrows'
import Buttons from 'components/Banners/SliderBanner/subcomponents/Buttons'
import clsx from 'clsx'

const TIMEOUT_DURATION = 5000

const SliderBanner = ({ className, placement, network = null }) => {
  const { i18n } = useSelector((state) => state.app)
  const [banners, setBanners] = useState([])
  const [currentBannerNum, setCurrentBannerNum] = useState(0)
  const [loading, setLoading] = useState(true)
  const breakpoints = useBreakpoint()
  const timeoutRef = useRef(null)

  const hasMoreThanOne = useMemo(() => banners.length > 1, [banners])

  const runAutoSlide = () => {
    timeoutRef.current = setTimeout(() => {
      setCurrentBannerNum((prev) => (prev < banners.length - 1 ? prev + 1 : 0))
      runAutoSlide()
    }, TIMEOUT_DURATION)
  }

  const handleEnter = () => clearTimeout(timeoutRef.current)

  const handleLeave = () => runAutoSlide()

  useEffect(() => {
    if (!network) {
      return
    }

    setBanners([])
    setLoading(true)

    BANNERS_API_V1.fetchBanners({
      params: { placement, network, locale: i18n },
    })
      .then(({ response }) => {
        setBanners(response?.data || [])
        setCurrentBannerNum(0)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [i18n, network])

  useEffect(() => {
    hasMoreThanOne && runAutoSlide()
  }, [banners, hasMoreThanOne])

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current)
  }, [])

  const currentBanner = useMemo(() => banners[currentBannerNum] ?? {}, [banners, currentBannerNum])

  const fontSizes = useMemo(() => {
    switch (true) {
      case breakpoints.sm:
        return { title: 'Subtitle-20', text: 'Body-15' }
      case !breakpoints.mdl:
        return { title: 'Subtitle-40', text: 'Body-20' }
      default:
        return { title: 'Subtitle-30', text: 'Body-15' }
    }
  }, [breakpoints])

  if (!loading && banners.length === 0) {
    return null
  }

  return loading ? null : (
    <div
      onMouseEnter={() => hasMoreThanOne && handleEnter()}
      onMouseLeave={() => hasMoreThanOne && handleLeave()}
      className={clsx(styles['container'], className, {
        [styles['container_loading']]: loading,
      })}
    >
      {currentBanner && (
        <div className={styles['background-container']}>
          <picture>
            <source
              media="(min-width: 1280px)"
              srcSet={currentBanner.image_desktop}
            />
            <source
              media="(min-width: 768px)"
              srcSet={currentBanner.image_tablet}
            />
            <source
              media="(max-width: 767px)"
              srcSet={currentBanner.image_mobile}
            />

            <img
              src={currentBanner?.image_desktop || currentBanner.image}
              alt={'pic'}
            />
          </picture>
        </div>
      )}
      <Grid
        className={styles['content']}
        direction={'column'}
        gap={24}
        justifyContent={'space-between'}
      >
        <Grid
          direction={'column'}
          justifyContent={'space-between'}
        >
          {!currentBanner ? null : (
            <>
              <Grid
                direction={'column'}
                gap={breakpoints.sm ? 8 : 12}
              >
                <Typography
                  variant={fontSizes.title}
                  color={'Label-Fifth'}
                  className={styles['title']}
                >
                  {currentBanner.title}
                </Typography>
                <Typography
                  variant={fontSizes.text}
                  component={'div'}
                >
                  {currentBanner.text}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <Grid
        className={'margin-top-16'}
        wrap={false}
        direction={'row'}
        alignItems={'center'}
        fluid={false}
        gap={breakpoints.fbs ? 20 : 44}
      >
        <Grid
          gap={16}
          direction={'row'}
          wrap={false}
          fluid={false}
        >
          <Buttons currentBanner={currentBanner} />
        </Grid>
        {banners.length > 1 && (
          <NavArrows
            banners={banners}
            currentBannerNum={currentBannerNum}
            setCurrentBannerNum={setCurrentBannerNum}
          />
        )}
      </Grid>
    </div>
  )
}

export default SliderBanner

SliderBanner.propTypes = {
  placement: PropTypes.string,
  network: PropTypes.string,
  className: PropTypes.string,
}
